//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { ColumnModel } from '../api/api.models'
import { Api, LocalDictionaryFactory } from '../api/api.module'

import { EventsNames, TemplateHelper, LocalizationHelper, ConfigHelper, TypeHelper } from '../../helpers'
import { CommonHelper } from '../../helpers/common-helper'

import { GridService } from '../../services/grid';
import { UserSessionService } from '../../services/user-session'
import { SystemService } from '../../services/system'
import { AlarmGroupService } from '../../services/alarm-group';

import { Enums, Activity } from '../../models/common'
import { OpAlarmGroup } from '../../models/core';

import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { BulkEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { GridTypes, ButtonsOptions, GridController } from '../common/grid/grid.controller'
import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../common/confirmation-popup2';

export interface AlarmGroupsScope extends ScopeBaseGrid {
}

export class AlarmGroupsController extends BaseGridController {

    //#region Consts

    private get ALARM_GROUPS_GRID_NAME() { return 'AlarmGroupsGrid' }

    //#endregion

    //#region Members

    private grid: GridController<OpAlarmGroup>

    //#endregion

    constructor(
        protected $scope: AlarmGroupsScope,
        protected $state: StateService,
        $timeout: ng.ITimeoutService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        private gridService: GridService,
        protected userSessionService: UserSessionService,
        protected systemService: SystemService,
        protected alarmGroupService: AlarmGroupService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, CommonHelper.IdAlarmGroup, referenceType)

        this.setEvents()
        this.setMainGridOptions()
        this.initializeConfirmationPopup()
    }

    //#region Events

    public onAdd(): void {
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, this.alarmGroupService.getCreateEditOptions())
    }

    public onConfirmDelete(): void {
        this.alarmGroupService.deleteAlarmGroup(this.grid.selectedItemId)
            .then(() => {
                this.onDeleted()
            })
    }

    public onDelete(): void {
        this.confirmationPopup.show()
    }

    public onDeleted(): void {
        this.dataGrid.refresh()
    }

    public onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length == 1) {
            this.$scope.$broadcast(
                EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
                this.alarmGroupService.getCreateEditOptions(this.grid.selectedItemId)
            )
        } else {
            const columnCount: number = this.dataGrid.columnCount()
            const columns: any[] = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i));
            }

            const gridData: any[] = TypeHelper.clone((this.dataGrid.getSelectedRowsData()))

            const bulkEditInitParams: BulkEditInitParams = {
                moduleName: Enums.SimaxModuleName.Alarm_Group,
                idReferenceType: this.referenceType,
                groupReferenceType: null,
                groupReferenceValue: null,
                selectedGridRows: gridData,
                gridColumns: columns,
                section: null,
                columnsReorderingDisabled: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmGroupModuleMainGrid, false),
                useBusinessObjects: true
            }

            this.$scope.$root.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    public onShowDetails(e: any): void {
        this.alarmGroupService.openDetails(this.grid.selectedItemId, this.grid.pageItemsIds)
    }

    public onInitialized(e: any, grid: GridController<OpAlarmGroup>): void {
        this.setGridId(e.tableId)

        this.dataGrid = e.component
        this.grid = grid
    }

    //#endregion

    //#region Confirmation Popup
    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion

    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items: any[] = [];

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSessionService),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    public getGridButtonsOptions(): ButtonsOptions {

        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_ADD) ? "['" + Activity[Activity.ALARM_GROUP_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DEL) ? "['" + Activity[Activity.ALARM_GROUP_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT) ? "['" + Activity[Activity.ALARM_GROUP_EDIT] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_LIST) ? "['" + Activity[Activity.ALARM_GROUP_LIST] + "']" : null,

            onShowClick: (e: any) => this.onShowDetails(e),
            onAddClick: () => this.onAdd(),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete()
        }
    }

    public setEvents(): void {
        this.$scope.$on(EventsNames.ALARM_GROUP_DELETED, (event: ng.IAngularEvent, ...args: any[]) => { this.onDeleted() })
        this.$scope.showDetails = (e: any) => { this.onShowDetails(e) }
        this.$scope.confirmDelete = () => { this.onConfirmDelete() }
    }

    public setMainGridOptions(): void {

        this.gridService
            .getColumnModelByCategory(
                [
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                    Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
                ],
                this.moduleName
            )
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns,
                    idReferenceType: this.referenceType,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.alarmGroupSaved,
                    name: this.ALARM_GROUPS_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    getDataSourceMethod: this.userSession.getDataProvider().AlarmGroup.getAlarmGroupFilter,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmGroupModuleMainGrid),

                    onInitialized: (e: any, grid: GridController<OpAlarmGroup>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => { this.onDataReady(data) },
                    onRowDoubleClick: (e: any) => { this.onShowDetails(e) },
                    contextMenuItems: (isTouchScreen: boolean = false) => { return this.getContextMenuItems(isTouchScreen) },
                }
            })
    }
    //#endregion
}